import type { AnswerMap, FieldDef, FieldType, TemplateDefinition } from './types.js';
import { isBlankAnswer, validateAnswer } from './values.js';

/**
 * Field types that may carry a `defaultValue`. Acknowledgements and signatures are excluded
 * on purpose (consent must be given explicitly), as are uploads, links, contacts, hours and
 * repeaters (their values reference other records or are too structured to prefill safely).
 */
export const DEFAULTABLE_FIELD_TYPES: ReadonlySet<FieldType> = new Set<FieldType>([
  'short_text',
  'long_text',
  'email',
  'phone',
  'url',
  'integer',
  'decimal',
  'currency',
  'date',
  'year',
  'single_select',
  'multi_select',
  'yes_no',
]);

/**
 * Why a field's `defaultValue` cannot be used, or null when it is valid (or absent).
 * Defaults are part of the template version, so they are immutable once published.
 */
export function defaultValueProblem(field: FieldDef, isChild = false): string | null {
  if (field.defaultValue === undefined) return null;
  if (isChild) return 'Default answers are not supported inside repeating groups.';
  if (!DEFAULTABLE_FIELD_TYPES.has(field.type)) return 'This question type cannot have a default answer.';
  if (field.audience === 'internal') return 'Internal questions cannot have a default answer.';
  const answer = { kind: 'value' as const, value: field.defaultValue };
  if (isBlankAnswer(answer)) return 'The default answer is empty.';
  if (field.options?.length && (field.type === 'single_select' || field.type === 'multi_select')) {
    const selected = (field.defaultValue as { selected?: unknown })?.selected;
    if (Array.isArray(selected) && selected.some((id) => field.options?.find((o) => o.id === id)?.retired)) return 'The default answer uses a retired option.';
  }
  const issue = validateAnswer(field, answer)[0];
  return issue ? `The default answer is not valid: ${issue.message}` : null;
}

/**
 * Applies template default answers to a fresh draft. Never overwrites an existing answer
 * (e.g. a shared-profile prefill), never touches internal-audience fields or repeater
 * children, and skips invalid defaults. Returns the new answers and the field ids it filled.
 */
export function applyDefaultAnswers(def: Pick<TemplateDefinition, 'sections'>, answers: AnswerMap): { answers: AnswerMap; applied: string[] } {
  const out: AnswerMap = { ...answers };
  const applied: string[] = [];
  for (const section of def.sections) {
    for (const field of section.fields) {
      if (field.defaultValue === undefined) continue;
      if (!isBlankAnswer(out[field.id]) || out[field.id]?.kind === 'na' || out[field.id]?.kind === 'unknown') continue;
      if (defaultValueProblem(field) !== null) continue;
      out[field.id] = { kind: 'value', value: JSON.parse(JSON.stringify(field.defaultValue)) as unknown };
      applied.push(field.id);
    }
  }
  return { answers: out, applied };
}
